// Company size is stored on the seller profile as "min,max" (either side may be empty), using
// the Lead411 EMPLOYEE_BUCKETS boundaries below. The profile page edits it with two dropdowns,
// and the chat proposes it as free text ("51-200", "500+", "up to 50"). Both go through here so
// the stored value is always one the dropdowns and the backend size filter can read.

export const SIZE_MIN_OPTS = [
  { label: 'No minimum', value: '' },
  { label: '1+',         value: '1' },
  { label: '11+',        value: '11' },
  { label: '51+',        value: '51' },
  { label: '201+',       value: '201' },
  { label: '501+',       value: '501' },
  { label: '1,001+',     value: '1001' },
  { label: '5,001+',     value: '5001' },
  { label: '10,001+',    value: '10001' },
];

export const SIZE_MAX_OPTS = [
  { label: 'No maximum',   value: '' },
  { label: 'Up to 10',     value: '10' },
  { label: 'Up to 50',     value: '50' },
  { label: 'Up to 200',    value: '200' },
  { label: 'Up to 500',    value: '500' },
  { label: 'Up to 1,000',  value: '1000' },
  { label: 'Up to 5,000',  value: '5000' },
  { label: 'Up to 10,000', value: '10000' },
  { label: '10,001+',      value: '10001' },
];

const MIN_VALUES = SIZE_MIN_OPTS.map(o => o.value).filter(Boolean).map(Number);
const MAX_VALUES = SIZE_MAX_OPTS.map(o => o.value).filter(Boolean).map(Number);

// Largest bucket floor at or below n, so "300+" widens to 201+ rather than narrowing to 501+.
// A number sitting on a bucket ceiling ("500+", "over 1000") means the next bucket up.
function snapMin(n) {
  if (MIN_VALUES.includes(n + 1)) return String(n + 1);
  let best = MIN_VALUES[0];
  for (const v of MIN_VALUES) if (v <= n) best = v;
  return String(best);
}

// Smallest bucket ceiling at or above n, so "up to 300" widens to 500 rather than cutting at 200.
function snapMax(n) {
  for (const v of MAX_VALUES) if (v >= n) return String(v);
  return String(MAX_VALUES[MAX_VALUES.length - 1]);
}

function parseNumber(token) {
  const m = String(token).toLowerCase().replace(/,/g, '').match(/(\d+(?:\.\d+)?)\s*(k)?/);
  if (!m) return null;
  const n = parseFloat(m[1]) * (m[2] ? 1000 : 1);
  return Number.isFinite(n) ? Math.round(n) : null;
}

// Free text or an already-stored value → "min,max". Returns '' when nothing usable is in it.
export function normalizeCompanySize(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return '';

  // Already the stored shape: "51,200", "501,", ",50". Only when both sides are real dropdown
  // values, so a headcount written with a thousands comma ("12,000") is read as a number.
  const stored = s.match(/^(\d*)\s*,\s*(\d*)$/);
  if (stored && (stored[1] || stored[2])
      && (!stored[1] || MIN_VALUES.includes(Number(stored[1])))
      && (!stored[2] || MAX_VALUES.includes(Number(stored[2])))) {
    return `${stored[1]},${stored[2]}`;
  }

  const nums = (s.replace(/(\d),(\d{3})/g, '$1$2').match(/\d+(?:\.\d+)?\s*k?/g) || [])
    .map(parseNumber)
    .filter(n => n !== null);
  if (nums.length === 0) return '';

  if (nums.length >= 2) {
    const [a, b] = nums[0] <= nums[1] ? [nums[0], nums[1]] : [nums[1], nums[0]];
    return `${snapMin(a)},${snapMax(b)}`;
  }

  const n = nums[0];
  if (/(under|up to|less than|fewer than|below|at most|max|<)/.test(s)) return `,${snapMax(n)}`;
  return `${snapMin(n)},`; // "500+", "over 1000", "at least 50", or a bare number
}

// "51,200" → "51 to 200 employees", "501," → "501+ employees", ",50" → "Up to 50 employees".
export function formatCompanySize(stored) {
  const [min = '', max = ''] = String(stored || '').split(',').map(x => x.trim());
  const fmt = v => Number(v).toLocaleString('en-US');
  if (min && max) return `${fmt(min)} to ${fmt(max)} employees`;
  if (min) return `${fmt(min)}+ employees`;
  if (max) return `Up to ${fmt(max)} employees`;
  return '';
}
